import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import type { LimitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { TypedMemo } from '../../common/types';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { formatAddOnCents } from '../../settings/tabs/addOns/addOnFormat';
import { ADD_ON_PRESET_PARAMS } from '../../settings/tabs/addOns/useAddOnPreset';
import { SMLSettingsTabId } from '../../settings/tabs/types';
import { SMLButton } from '../components/primitives/SMLButton';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt';
import { LimitUpsellConfirm } from './LimitUpsellConfirm';
import {
  limitUpsellFillPercent,
  limitUpsellSub,
  limitUpsellTitle,
  quotaMonthName,
  type LimitUpsellContext,
} from './limitUpsellCopy';
import { limitUpsellMessages as messages } from './limitUpsellMessages';
import type { LimitUpsellState } from './useLimitUpsell';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the limit upsell; each part uses its own slice of it */
import styles from './LimitUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface LimitUpsellFit {
  /** The label of the step; default "Plan only what fits". */
  label?: string;
  onClick: () => void;
}

/** One more step of the card, drawn as a quiet link button ("Buy a credit pack"). */
export interface LimitUpsellAction {
  label: string;
  onClick: () => void;
}

interface LimitUpsellCardProps {
  shortfall: LimitShortfall;
  context: LimitUpsellContext;
  state: LimitUpsellState;
  fit?: LimitUpsellFit | null;
  /** More steps after the main one (the credit packs). */
  extraActions?: LimitUpsellAction[];
}

/**
 * The inline card of a flow that does not fit the posts or the video credits left (design
 * limit-upsell a1, a6-a12). It shows the numbers and the one step that helps: the add-on (the
 * owner of a web plan), the plans (Free, the trial, a store plan), or the owner note (a member).
 * "Plan only what fits" is always there when the flow has a smaller plan. It never blocks.
 */
const LimitUpsellCardComponent: React.FC<LimitUpsellCardProps> = ({ shortfall, context, state, fit, extraActions }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgrade = useUpgradePrompt();
  const { offer, addOn, addState } = state;
  const isCredits = shortfall.kind === 'videoCredits';
  const month = quotaMonthName(intl);
  const sub = limitUpsellSub(shortfall, context, offer);
  const fill = limitUpsellFillPercent(shortfall);
  const isConfirming = addOn !== null && (addState === 'confirm' || addState === 'adding' || addState === 'failed');

  const seePlans = useCallback(
    () => openUpgrade(isCredits ? SMLUpgradeFeature.videoCredits : SMLUpgradeFeature.scheduledPosts),
    [isCredits, openUpgrade]
  );
  const otherAmount = useCallback(() => {
    if (addOn === null) {
      return;
    }
    router.push(SMLRoutes.settings, {
      tab: SMLSettingsTabId.subscription,
      [ADD_ON_PRESET_PARAMS[addOn.kind]]: String(addOn.next[addOn.kind]),
    });
  }, [addOn, router]);

  let addLabel = '';
  if (addOn !== null) {
    const price = formatAddOnCents(intl, addOn.addPeriodCents);
    const yearly = addOn.interval === 'yearly';
    const descriptor = isCredits
      ? yearly
        ? messages.addCreditsYear
        : messages.addCreditsMonth
      : yearly
        ? messages.addPostsYear
        : messages.addPostsMonth;
    addLabel = intl.formatMessage(descriptor, { count: addOn.add, price });
  }

  return (
    <section className={clsx(styles.card, isCredits && styles.cardBad)} data-testid="limit-upsell" role="status">
      <div className={styles.head}>
        <span className={clsx(styles.mark, isCredits && styles.markBad)} aria-hidden="true">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </span>
        <p className={styles.title}>{limitUpsellTitle(intl, shortfall, context, month)}</p>
        {sub !== null && <p className={styles.sub}>{intl.formatMessage(sub)}</p>}
      </div>
      {context !== 'refused' && (
        <div className={styles.meter}>
          <div className={styles.bar}>
            <span className={styles.fill} style={{ width: `${fill}%` }} />
            <span className={styles.short} style={{ left: `${fill}%` }} />
          </div>
          <div className={styles.legend}>
            <span>{intl.formatMessage(messages.legendLeft, { count: shortfall.left })}</span>
            <span>{intl.formatMessage(messages.legendShort, { count: shortfall.short })}</span>
          </div>
        </div>
      )}
      {isConfirming && addOn !== null ? (
        <LimitUpsellConfirm
          addOn={addOn}
          plan={state.plan}
          isAdding={addState === 'adding'}
          hasFailed={addState === 'failed'}
          onConfirm={state.confirm}
          onCancel={state.cancel}
        />
      ) : (
        <div className={styles.actions}>
          {offer === 'addOn' && addOn !== null && (
            <>
              <SMLButton variant="primary" className={styles.primary} onClick={state.openConfirm}>
                {addLabel}
              </SMLButton>
              <button type="button" className={styles.link} onClick={otherAmount}>
                {intl.formatMessage(messages.otherAmount)}
              </button>
            </>
          )}
          {(offer === 'plans' || offer === 'higherPlan') && (
            <SMLButton variant="primary" className={styles.primary} onClick={seePlans}>
              {intl.formatMessage(messages.seePlans)}
            </SMLButton>
          )}
          {extraActions?.map((action) => (
            <button key={action.label} type="button" className={styles.quiet} onClick={action.onClick}>
              {action.label}
            </button>
          ))}
          {fit !== null && fit !== undefined && (
            <button type="button" className={styles.quiet} onClick={fit.onClick}>
              {fit.label ?? intl.formatMessage(messages.fitDefault)}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export const LimitUpsellCard = TypedMemo(LimitUpsellCardComponent);
